<template>
  <div class="hourly-wage-input">
    <button
      type="button"
      class="wage-btn minus"
      aria-label="시급 1원 감소"
      :disabled="disabled"
      @mousedown.prevent="onHoldStart(-1)"
      @mouseup="holdStop"
      @mouseleave="holdStop"
      @touchstart.prevent="onHoldStart(-1)"
      @touchend="holdStop"
      @touchcancel="holdStop"
    >
      -
    </button>
    <input
      :value="modelValue"
      type="text"
      :disabled="disabled"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      @click.stop
    />
    <button
      type="button"
      class="wage-btn plus"
      aria-label="시급 1원 증가"
      :disabled="disabled"
      @mousedown.prevent="onHoldStart(1)"
      @mouseup="holdStop"
      @mouseleave="holdStop"
      @touchstart.prevent="onHoldStart(1)"
      @touchend="holdStop"
      @touchcancel="holdStop"
    >
      +
    </button>
  </div>
</template>

<script setup lang="ts">
import { useHoldRepeat } from '@/composables/wage/useHoldRepeat';
import { formatWageNumber, parseWageNumber } from '@/composables/wage/useWageCalculator';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    minHourlyWage?: number;
    disabled?: boolean;
  }>(),
  {
    minHourlyWage: 0,
    disabled: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [];
}>();

const clampHourly = (value: number): number => {
  const min = props.minHourlyWage ?? 0;
  let next = value;
  if (min > 0 && next < min) {
    next = min;
  }
  if (next < 0) {
    next = 0;
  }
  return next;
};

const applyDelta = (delta: number) => {
  const current = parseWageNumber(props.modelValue);
  emit('update:modelValue', formatWageNumber(clampHourly(current + delta)));
};

const holdUp = useHoldRepeat(() => applyDelta(1));
const holdDown = useHoldRepeat(() => applyDelta(-1));

const onHoldStart = (delta: number) => {
  if (props.disabled) return;
  (delta > 0 ? holdUp : holdDown).start();
};

const holdStop = () => {
  holdUp.stop();
  holdDown.stop();
};

const onInput = (e: Event) => {
  const raw = (e.target as HTMLInputElement).value.replace(/[^\d]/g, '');
  const num = raw ? parseInt(raw, 10) : 0;
  emit('update:modelValue', num ? formatWageNumber(num) : '');
};

const onBlur = () => {
  const hourly = parseWageNumber(props.modelValue);
  const min = props.minHourlyWage ?? 0;
  if (min > 0 && hourly > 0 && hourly < min) {
    alert(`시급은 최저시급(${formatWageNumber(min)}원) 이상이어야 합니다.`);
    emit('update:modelValue', formatWageNumber(min));
  }
  emit('blur');
};

const onFocus = (e: Event) => {
  const el = e.target as HTMLInputElement;
  setTimeout(() => el.select(), 0);
};
</script>
